import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, colors, FormControl, FormControlLabel, InputLabel, MenuItem, Select, selectClasses, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import {
  ActionItem,
  LeaseLifecycle,
  Unit,
  UserData,
} from '@urbanmix-tech/shared-js';

import {
  convertPseudoDateToRealDate,
  getLongNumberStr,
} from '@/helpers/common';

import {
  ITableCell,
  ITableRow,
  VirtualizedTable,
  ColumnSeletion
} from '@/components/shared/Table';

// Redux
import { useDispatch } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setApartmentData,
  setVisualizer,
  setHoveredUnitId,
  setBuildingVisual,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
  ColorGradientVisualizer,
  Visualizer,
} from '@urbanmix-tech/um-embedded';

import { GenericDistinctVisualizer,colorScales } from './BuildingVisualizers'
import type { ColorScaleOptions } from './BuildingVisualizers';

import { parse } from 'csv-parse/sync';
import { ReportsTabContent } from '../UnitTabs/ReportsTabContent';
import { ClassNames } from '@emotion/react';
import { CustomReport } from './CustomReportTabContent';


const steps: Record<string, number> = {
  'waiting_to_send': 1,
  'waiting_for_response': 2,
  'waiting_for_tenant_signature': 3,
  'waiting_for_countersignature': 4,
  'upload_lease': 5,
};
  
let stepsNum: Record<number,string> = {}
  
for(const key in steps) {
    const value = steps[key];
    stepsNum[value] = key;   
}

let stepToStatus : Record<number,string> = {
    1: 'Started',
    2: 'Sent to tenant',  
    3: 'Tenant signed',
    4: 'Completed',
    5: 'Completed',
}

type CustomAggregatedReportTabContentProps = {  
  report : CustomReport;
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
  colorScale: ColorScaleOptions;
};

export function CustomAggregatedReportTabContent({
  report,  
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
  colorScale='default',
}: CustomAggregatedReportTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  const handleClickRow = (apt: any) => {        
    dispatch(setSelectedUnit({ apartmentId: apt.id }));    
  };

  const [columns,setColumns] = useState<ITableCell[]>([]);
  const [visualizer,setVisualizer] = useState<Visualizer | null>(null);

  const [tableData, setTableData] = useState({
    columns: [] as ITableCell[],
    rows: [] as ITableRow[],
    grouped: {} as Map<string,ITableRow[]>,
  })

  // read csv from report.data
  useEffect(()=> {
    
    let cols : ITableCell[] = []

    const rawRecords = parse(report.data) as string[][];

    const headers = rawRecords[0];

    if (report.columns) {
      // create columns from report.columns
      cols = report.columns.map((col) => {
        return col;
      })
    } else {
      // create columns from first line of csv      
      cols = headers.map((header) => {
        return {
          label: header,
          sortable: true,
          className: 'bg-gray-950',
          showCheckbox: true,
        }
      })                    
    }

    console.log('columns cols',cols)

    const sourceRows : ITableRow[] = rawRecords.slice(1).map((record) => {
      // const newRecord: Record<string, unknown> = {};
      let newRows : ITableRow[] = [] 

      let data : Record<string, ITableCell> = {}
      record.forEach((item, index) => {
        if (index >= cols.length) {
          return;
        }
        const col = cols[index];
        const colKey = col.label as string;    

        let value : any = item;

        if (col.kind === 'currency') {
          value = parseCurrencyString(item) ?? 0
          // console.log('currency',value)
        }
        
        
        data[colKey] = {
          label: item,
          value: value,
          className: '',
        } as ITableCell;
        
        })
        return {
          id: record[0],
          data: data,
        }
      });
      
      let keys = []
      // lets aggretate the data according to the aggregate proprty 
      sourceRows.forEach((row) => {
        // console.log('row',row)                
        report.aggregate?.forEach((column) => {
          keys.push(row.data[column].value)          
        })        
      })


      let data : Map<string,ITableRow[]> = new Map()

      sourceRows.forEach((row) => {
        let keys : any[] = [] 
        report.aggregate?.forEach((column) => {
          keys.push(row.data[column].value)          
        }) 
        let key = keys.join('-')
        if (!data.has(key)) {
          data.set(key,[])
        }
        data.get(key)?.push(row)

      });
      // console.log('rows',rows)

    setTableData({
      columns: cols,
      rows: sourceRows,
      grouped: data
    });
      
  },[report]);
  
  const [selectedColumn, setSelectedColumn] = useState<ColumnSeletion | null>(null);

  useEffect(() => {
    if (!tableData.rows || tableData.rows.length === 0 || !report.aggregate || report.aggregate.length === 0) {
      return;
    }

    const counts = Array.from(tableData.grouped).map(([key,rows]) => {      
      return {
        key: key,
        count: rows.length
      };
    });

    const colDef : ITableCell | undefined = tableData.columns.find((col) => col.label === report.aggregate![0]);
    const data = tableData.rows.map((row) => {
            
      const value = report.aggregate ? row.data[report.aggregate[0]].value : '';
      const label = colDef?.label ?? '';
      // console.log(label,value)
      return {
        apartmentId: row.data['Unit'].value,
        value: value,
        label
      };
    })
    

    const values = data.map((row) => row.value)
    const distinctValues = new Set(values);
    const colors = colorScales[colorScale];
          
    let visualizer = null;
    // if less than 10 distinct values
    if (distinctValues.size < 10) {
      let colorMap = new Map();
      let arrDistinctValues = Array.from(distinctValues);
      arrDistinctValues.forEach((value, index) => {
        colorMap.set(value,colors[index])
        });
      // console.log('setting visualizer for distinct values', colorMap);
      visualizer = new GenericDistinctVisualizer(colorMap);
    } else {

      const colDef : ITableCell | undefined = tableData.columns.find((col) => col.label === selectedColumn?.label);
      // console.log('colDef',colDef)
        
      if (colDef && colDef.kind === 'currency') {
        console.log('setting visualizer for currency values',values);
        const minValue = values.reduce((acc, value) => {
          if (value != 0) {
            return Math.min(acc as number, value as number);
          }
          return acc;
        }, Infinity);
        const maxValue = values.reduce((acc, value) => {
          return Math.max(acc as number, value as number);
        }, -Infinity);
        console.log('setting visualizer for non distinct values',values, minValue,maxValue);
        // visualizer = new ColorGradientVisualizer(['#313695', '#A50026'], minValue, maxValue);
        visualizer = new ColorGradeVisualizer(colors, minValue, maxValue);
      } else {
        visualizer = new ColorGradeVisualizer(colors, '', '');
      }
      
    }          

    setVisualizer(visualizer);

    dispatch(setBuildingVisual({
      apartmentData: data,
      visualizer: visualizer,
      }))
    
  //    
    setTimeout(() => {
      dispatch(setHighlightedUnits(data));
    }, 500);
  
  },[tableData,colorScale,selectedColumn])
// 

  return (
    <div className="unit-mix h-[calc(100vh-112px)] w-full" ref={ref}>
      <div className="mb-4 relative z-10 top-0 right-0 flex flex-row gap-4">       
        <div className="flex flex-row gap-4 flex-1 justify-end">
         
        </div>
      </div>

      {/* interate over tableData.grouped */}
      <div className='center flex flex-col px-16 py-16 max-w[80%]'>
      <div className='border border-zinc-500 border-r-0 border-l-0'>
        {Array.from(tableData.grouped).map(([key,rows]) => {
          console.log('key',key,visualizer)
          let color : string; 
          if (visualizer && visualizer instanceof GenericDistinctVisualizer) {
            color = (visualizer! as GenericDistinctVisualizer).getColor(key) ?? '#0000FF';
          } else if (visualizer && visualizer instanceof ColorGradeVisualizer) {
            color = (visualizer! as ColorGradeVisualizer).getColor(key) ?? '#0000FF';
          } else {
            color = '#0000FF';
          }
          
          return (
            
                <div key={key} className="flex flex-row border border-black ">
                <div className="w-16 flex-none" style={{ backgroundColor: color }}></div>
                <div className="px-2 py-1 font-semibold flex-grow">
                  {key}
                </div>
                <div className="w-16 px-2 py-1 text-right flex-none">
                  {key === 'Future Occupancy' ? `${rows.length.toFixed(2)}%` : rows.length}
                </div>
                </div>
            
          );
        })}
        </div>
      </div>
    </div>
  );
}


function parseCurrencyString(str: string) : number | null {
  // Remove anything that's not digits, minus sign, or decimal point
  const cleanedString = str.replace(/[^0-9.-]+/g, '');
  
  // Parse the cleaned string to float
  const number = parseFloat(cleanedString);

  return isNaN(number) ? null : number;
}